import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { Input2026Props } from "@src/components/atoms/input-2026/input-2026";
import { TextareaProps } from "@src/components/atoms/textarea/textarea";
import { FIRMWARE } from "@src/cloud-connect/ota-update/constants";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { OtaDrawerDataInterface } from "@src/types/ota-update";

interface UseOTADrawerFormProps {
  values: OtaDrawerDataInterface;
  setValues: React.Dispatch<React.SetStateAction<OtaDrawerDataInterface>>;
}

interface UseOTADrawerFormResult {
  uploadFileText: string;
  firmwareFields: Input2026Props[];
  releaseNotesFields: TextareaProps[];
}

export const useOTADrawerForm = ({
  values,
  setValues,
}: UseOTADrawerFormProps): UseOTADrawerFormResult => {
  const i18n = useI18N();

  const otaActiveView = useSelector(
    (state: RootState) => state.yourSlice.otaActiveView,
  );

  const texts = {
    versionText: i18n(
      `modules.otaDrawerForm.${
        otaActiveView === FIRMWARE
          ? "firmwareVersionText"
          : "configurationVersionText"
      }`,
    ),
    versionPlaceholderText: i18n(
      "modules.otaDrawerForm.versionPlaceholderText",
    ),
    nameText: i18n(
      `modules.otaDrawerForm.${
        otaActiveView === FIRMWARE
          ? "firmwareNameText"
          : "configurationNameText"
      }`,
    ),
    namePlaceholderText: i18n("modules.otaDrawerForm.namePlaceholderText"),
    releaseNotesText: i18n("modules.otaDrawerForm.releaseNotesText"),
    releaseNotesPlaceholderText: i18n(
      "modules.otaDrawerForm.releaseNotesPlaceholderText",
    ),
    improvmentsText: i18n("modules.otaDrawerForm.improvmentsText"),
    improvmentsPlaceholderText: i18n(
      "modules.otaDrawerForm.improvmentsPlaceholderText",
    ),
    generalText: i18n("modules.otaDrawerForm.generalText"),
    generalPlaceholderText: i18n(
      "modules.otaDrawerForm.generalPlaceholderText",
    ),
    fixesText: i18n("modules.otaDrawerForm.fixesText"),
    fixesPlaceholderText: i18n("modules.otaDrawerForm.fixesPlaceholderText"),
    uploadFileText: i18n("modules.otaDrawerForm.uploadFileText"),
  };

  const updateValue = useCallback(
    (key: keyof OtaDrawerDataInterface, value: string) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [setValues],
  );

  const firmwareFields: Input2026Props[] = [
    {
      id: "firmware_name",
      label: texts.nameText,
      placeholder: texts.namePlaceholderText,
      value: values.name,
      onChange: (e) => updateValue("name", e.target.value),
    },
    {
      id: "version",
      label: texts.versionText,
      placeholder: texts.versionPlaceholderText,
      value: values.version,
      onChange: (e) => updateValue("version", e.target.value),
    },
  ];

  const releaseNotesFields: TextareaProps[] = [
    {
      id: "release_notes_intro",
      label: texts.releaseNotesText,
      placeholder: texts.releaseNotesPlaceholderText,
      value: values.release_notes_intro,
      onChange: (e) => updateValue("release_notes_intro", e.target.value),
    },
    {
      id: "release_notes_improvements",
      label: texts.improvmentsText,
      placeholder: texts.improvmentsPlaceholderText,
      value: values.release_notes_improvements,
      onChange: (e) =>
        updateValue("release_notes_improvements", e.target.value),
    },
    {
      id: "release_notes_fixes",
      label: texts.fixesText,
      placeholder: texts.fixesPlaceholderText,
      value: values.release_notes_fixes,
      onChange: (e) => updateValue("release_notes_fixes", e.target.value),
    },
    {
      id: "release_notes_general",
      label: texts.generalText,
      placeholder: texts.generalPlaceholderText,
      value: values.release_notes_general,
      onChange: (e) => updateValue("release_notes_general", e.target.value),
    },
  ];

  return {
    uploadFileText: texts.uploadFileText,
    firmwareFields,
    releaseNotesFields,
  };
};
